/* globals document, Image */
import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { styled } from 'baseui';
import { Block } from 'baseui/block';
import { Modal, ModalBody } from 'baseui/modal';
import { CONTROL_ACTION_ICON, PDF_ACTION_TYPE } from '../constant/constant';
import { ControlPanel } from '../image/control-panel.react';
// TO do As fractal does not support assetURl, using CDN for now until gets a solution
import { downloadFromUrl } from '../image/utils';
import { reducer } from './reducer';
type Props = {
  src: string;
  downloadFile?: boolean;
};
export type PDFRendererState = {
  scale: number;
  minScale: number;
  maxScale: number;
  rotation: number;
  expanded: boolean;
};
const initialState: PDFRendererState = {
  scale: 1,
  minScale: 0.25,
  maxScale: 3,
  rotation: 0,
  expanded: false
};
const Container = styled<typeof Block, {
  $expand: boolean;
}>(Block, ({
  $theme,
  $expand
}) => {
  return {
    position: 'relative',
    width: '100%',
    height: $expand ? '100%' : '550px',
    cursor: 'pointer'
  };
});
const DocumentContainer = styled<typeof Block, {
  $expand: boolean;
}>(Block, ({
  $theme,
  $expand
}) => {
  return {
    position: 'absolute',
    width: '100%',
    height: '100%',
    overflow: 'auto',
    cursor: 'pointer',
    display: $expand ? 'flex' : 'block',
    justifyContent: 'center'
  };
});
const modalOverrides = {
  Root: {
    style: {
      // default is 'fixed' which causes modal to overflow parent if consumer is using layout system like f7-ux
      position: 'absolute'
    }
  },
  DialogContainer: {
    style: {
      position: 'absolute',
      width: '100%',
      height: '100%'
    }
  },
  Dialog: {
    style: {
      width: '100%',
      height: '100%',
      margin: 0
    }
  }
};
export default function PDFRenderer(props: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    import('pdfjs-dist/build/pdf.worker.min.mjs').then(() => {
      import('pdfjs-dist/build/pdf.mjs').then(PDFJS => {
        PDFJS.GlobalWorkerOptions.workerSrc = 'pdfjs-dist/build/pdf.worker.min.mjs';
      });
    });
  }, []);
  return <React.Fragment>
      <Content {...props} state={state} dispatch={dispatch} />
      <Modal focusLock={false} isOpen={state.expanded} onClose={() => {
      dispatch({
        type: PDF_ACTION_TYPE.RESET_EXPANDED
      });
    }} closeable={false} overrides={modalOverrides}>
        <ModalBody style={{
        height: '100%',
        margin: 0
      }}>
          <Content {...props} state={state} dispatch={dispatch} />
        </ModalBody>
      </Modal>
    </React.Fragment>;
}
function Content({
  src,
  state,
  dispatch,
  downloadFile = false
}) {
  const containerRef = useRef<HTMLDivElement>();
  const [displayWidth, setDisplayWidth] = useState(0);
  const [pageCount, setPageCount] = React.useState(0);
  useEffect(() => {
    if (containerRef.current) {
      setDisplayWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef, state.expand]);
  const onActionClick = icon => {
    switch (icon) {
      case CONTROL_ACTION_ICON.ZOOM_IN:
        {
          dispatch({
            type: PDF_ACTION_TYPE.ON_ZOOM_IN
          });
          break;
        }
      case CONTROL_ACTION_ICON.ZOOM_OUT:
        {
          dispatch({
            type: PDF_ACTION_TYPE.ON_ZOOM_OUT
          });
          break;
        }
      case CONTROL_ACTION_ICON.FULLSCREEN:
        {
          dispatch({
            type: PDF_ACTION_TYPE.ON_ZOOM_FIT
          });
          break;
        }
      case CONTROL_ACTION_ICON.ROTATE:
        {
          dispatch({
            type: PDF_ACTION_TYPE.ON_PDF_ROTATE
          });
          break;
        }
      case CONTROL_ACTION_ICON.EXPAND:
      case CONTROL_ACTION_ICON.COLLAPSE:
        {
          dispatch({
            type: PDF_ACTION_TYPE.ON_EXPAND_TOGGLE
          });
          break;
        }
      case CONTROL_ACTION_ICON.DOWNLOAD:
        {
          downloadFromUrl(src);
          break;
        }
    }
  };
  return <Container ref={containerRef} $expand={state.expanded} data-testid="container">
      <DocumentContainer $expand={state.expanded}>
        <Document file={src} onLoadSuccess={({
        numPages
      }) => setPageCount(numPages)}>
          {Array.from(new Array(pageCount), (el, index) => {
          return <Page scale={state.scale} object-fit="fill" key={`pdfPage_${index}`} pageNumber={index + 1} renderAnnotationLayer={false} renderTextLayer={false} rotate={state.rotation} width={displayWidth} />;
        })}
        </Document>
      </DocumentContainer>
      <ControlPanel onClick={onActionClick} expanded={state.expanded} downloadFile={downloadFile} />
    </Container>;
}